import { Capacitor } from '@capacitor/core';

/**
 * GHIN login stored on this device only, keyed by league user so two people
 * sharing a phone never see each other's login.
 *
 * localStorage is the store reads and writes rely on — per app sandbox, the
 * same place ghin.com's own "Remember Me" keeps it. On native it is mirrored
 * into the iOS Keychain / Android Keystore through
 * @aparajita/capacitor-secure-storage, in the background and never awaited.
 * The server never stores it; it is sent with each post and discarded.
 *
 * The plugin was the store of record until it wedged on a real device, in read
 * and in write, without its bridge calls ever answering or their ceilings
 * firing. Nothing the player waits on may depend on it.
 */
const PREFIX = 'gchl_ghin_credentials';
const NATIVE_TIMEOUT_MS = 4000;

const keyFor = (userId) => `${PREFIX}:${userId}`;

class TimeoutError extends Error {}

function withTimeout(promise, label) {
  let timer;
  return Promise.race([
    Promise.resolve(promise),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new TimeoutError(`${label} did not answer`)), NATIVE_TIMEOUT_MS);
    }),
  ]).finally(() => clearTimeout(timer));
}

async function secureStorage() {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { SecureStorage } = await withTimeout(
      import('@aparajita/capacitor-secure-storage'),
      'secure storage plugin load',
    );
    return SecureStorage ?? null;
  } catch {
    return null; // plugin missing from this binary, or wedged
  }
}

function parse(raw) {
  if (!raw) return null;
  const value = typeof raw === 'string' ? tryJson(raw) : raw;
  return value?.emailOrGhin && value?.password ? value : null;
}

function tryJson(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readLocal(key) {
  try {
    return parse(localStorage.getItem(key));
  } catch {
    return null;
  }
}

/**
 * Local storage is read first: it holds a value only when the Keychain write
 * failed (setGhinCredentials clears it on a successful Keychain write), and it
 * answers instantly. Going to the plugin first cost up to two 4s timeouts
 * before the caller learned anything.
 */
export async function getGhinCredentials(userId) {
  if (!userId) return null;
  const key = keyFor(userId);
  const local = readLocal(key);
  if (local) return local;
  try {
    // One ceiling over the whole native path, not just the calls inside it:
    // this function must settle for every caller, so a store that goes quiet
    // reads as "no saved login" instead of leaving the UI waiting.
    return await withTimeout(readSecure(key), 'device store read');
  } catch (err) {
    console.warn('[GHIN] Device store read failed', err);
    return null;
  }
}

async function readSecure(key) {
  const storage = await secureStorage();
  if (!storage) return null;
  try {
    return parse(await withTimeout(storage.getItem(key), 'Keychain read'));
  } catch (err) {
    console.warn('[GHIN] Keychain read failed', err);
    return null;
  }
}

/**
 * Local storage is written first and the caller never waits on the Keychain.
 *
 * The plugin has been seen to wedge on a real device — a write that never
 * answered left the Reconnect button spinning and the login unsaved, and the
 * 4s ceiling on the bridge call did not rescue it. The Keychain copy is now a
 * bonus written in the background; the local copy is the one reads rely on and
 * is kept either way, so a store that goes quiet can never lose the login.
 * Throws only if local storage itself refuses the write.
 */
export function setGhinCredentials(userId, { emailOrGhin, password }) {
  if (!userId) throw new Error('Not signed in');
  const key = keyFor(userId);
  const raw = JSON.stringify({ emailOrGhin, password });
  localStorage.setItem(key, raw);
  mirrorToSecureStore(key, raw);
  return Promise.resolve('local');
}

function mirrorToSecureStore(key, raw) {
  (async () => {
    const storage = await secureStorage();
    if (!storage) return;
    try {
      await withTimeout(storage.setItem(key, raw), 'Keychain write');
    } catch (err) {
      console.warn('[GHIN] Keychain write failed, login stays in local storage', err);
    }
  })();
}

/** Clears the copy reads use straight away; the Keychain copy follows. */
export function clearGhinCredentials(userId) {
  if (!userId) return Promise.resolve();
  const key = keyFor(userId);
  try { localStorage.removeItem(key); } catch { /* ignore */ }
  (async () => {
    const storage = await secureStorage();
    if (!storage) return;
    try {
      await withTimeout(storage.removeItem(key), 'Keychain delete');
    } catch (err) {
      console.warn('[GHIN] Keychain delete failed', err);
    }
  })();
  return Promise.resolve();
}
